import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Banner } from '../config/Banner';
import { navigationSections } from '../config/navigation';
import SidebarItem from '../components/ui/sidebar-item';
import { SettingsProvider } from '../services/SettingsContext';
import { TokenProvider } from '../services/TokenManager';
import { PocCopilotChat } from '@/components/copilot/PocCopilotChat';
import { CopilotProvider, useCopilotContext, buildValueStreamSystemMessage } from '@/contexts/CopilotContext';
import { AlertsProvider, useAlerts } from '@/contexts/AlertsContext';
import { AlertsPanel } from '@/components/alerts/AlertsPanel';
import { Toaster } from '@/components/ui/toaster';
import { useAuth } from '../services/AuthContext';
import { useSettings } from '../services/SettingsContext';
import { ModelSelectorPage } from '../components/settings/ModelSelectorPage';

// Import all page components
import { BusinessModelSection } from '../components/businessModel/BusinessModelSection';
import { DataManagementSection } from '../components/dataManagement/DataManagementSection';
import { MetricsSection } from '../components/metrics/MetricsSection';
import { ValueStreamPerformancePage } from '../components/valueStreamPerformance/ValueStreamPerformancePage';
import { SettingsSection } from '../components/settings/SettingsSection';

function AppLayoutContent() {
  const { isAuthenticated, isLoading, isRedirecting, error, login } = useAuth();
  const { apiSettings, showModelSelector } = useSettings();

  if (isLoading || isRedirecting) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-background">
        <p className="text-sm text-destructive">{error}</p>
        <button
          className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
          onClick={login}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  if (!apiSettings.selectedModelId || showModelSelector) return <ModelSelectorPage />;

  return <AppLayoutMain />;
}

function AppLayoutMain() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize active section from URL or default to 'valueStreamPerformance'
  const getInitialSection = () => {
    const sectionParam = searchParams.get('section');
    const validSections = navigationSections.map(s => s.id);
    return sectionParam && validSections.includes(sectionParam) ? sectionParam : 'valueStreamPerformance';
  };

  const [activeSection, setActiveSection] = useState(getInitialSection);
  const [isCopilotExpanded, setIsCopilotExpanded] = useState(true);
  const { alerts, isAlertsPanelOpen, toggleAlertsPanel, setAlertsPanelOpen } = useAlerts();

  const toggleCopilotExpanded = () => {
    setIsCopilotExpanded(!isCopilotExpanded);
  };

  // Sync active section to URL when it changes
  useEffect(() => {
    // Read current URL params at update time to avoid race conditions
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('section', activeSection);
    setSearchParams(currentParams, { replace: true });
  }, [activeSection, setSearchParams]);

  // Update active section when URL changes (e.g., browser back/forward)
  useEffect(() => {
    const sectionParam = searchParams.get('section');
    const validSections = navigationSections.map(s => s.id);
    if (sectionParam && validSections.includes(sectionParam) && sectionParam !== activeSection) {
      setActiveSection(sectionParam);
    }
  }, [searchParams]);

  const renderSection = () => {
    switch (activeSection) {
      case 'model':
        return <BusinessModelSection />;
      case 'data-management':
        return <DataManagementSection />;
      case 'metrics':
        return <MetricsSection />;
      case 'valueStreamPerformance':
        return <ValueStreamPerformancePage />;
      case 'settings':
        return <SettingsSection />;
      default:
        return <ValueStreamPerformancePage />;  
    }
  };

  const { graphData, valueStreamName, metricsData } = useCopilotContext();
  const systemMessage = graphData ? buildValueStreamSystemMessage(graphData, valueStreamName, metricsData) : undefined;
  const copilotTitle = graphData ? "Value Stream Assistant" : "Copilot";
  const copilotPlaceholder = graphData ? "Ask about this value stream..." : "Load a value stream to start chatting...";

  return (
    <>
      <main className="fixed inset-0 flex flex-col overflow-hidden bg-background text-left">
        <Banner
          isCopilotExpanded={isCopilotExpanded}
          onToggleCopilotExpanded={toggleCopilotExpanded}
          isAlertsPanelOpen={isAlertsPanelOpen}
          onToggleAlertsPanel={toggleAlertsPanel}
          alertsCount={alerts.length}
        />
        <div className="flex-1 flex min-h-0 isolate">

          <aside className="w-64 border-r border-gray-200 h-full overflow-auto flex-shrink-0 relative z-[60]">
            <nav className="space-y-1 p-4" style={{ fontFamily: 'Segoe UI, system-ui' }}>
              {navigationSections.map((section) => (
                <SidebarItem
                  key={section.id}
                  icon={section.icon}
                  label={section.label}
                  active={activeSection === section.id}
                  onClick={() => setActiveSection(section.id)}
                />
              ))}
            </nav>
            <div id="portal-root" />
          </aside>
          <div className="flex-1 overflow-auto">
            {renderSection()}
          </div>

          {/* Copilot Chat panel - right side overlay */}
          {isCopilotExpanded && (
            <div className="fixed right-0 w-96 bg-white border-l shadow-lg z-50 flex flex-col" style={{ height: 'calc(100vh - 96px)' }}>
              <div className="flex-1 overflow-hidden">
                <PocCopilotChat 
                  systemMessage={systemMessage}
                  title={copilotTitle}
                  placeholder={copilotPlaceholder}
                  disabled={!graphData}
                />
              </div>
            </div>
          )}

          {/* Alerts panel - right side overlay */}
          {isAlertsPanelOpen && (
            <AlertsPanel onClose={() => setAlertsPanelOpen(false)} onOpenCopilot={() => setIsCopilotExpanded(true)} />
          )}
        </div>
      </main>
    </>
  );
}

export const AppLayout: React.FC = () => {
  return (
    <SettingsProvider>
      <TokenProvider>
        <CopilotProvider>
          <AlertsProvider>
            <AppLayoutContent />
            <Toaster />
          </AlertsProvider>
        </CopilotProvider>
      </TokenProvider>
    </SettingsProvider>
  );
};